<!--
Tooltips have a delay before they show up, to avoid annoying the user when moving the mouse around.
This delay cannot be implemented in CSS alone, because we need to delay the "show" state being set. If we would do
it with CSS only, the tooltip would be added to DOM immediately, and register listener etc., which is not desired if
the user only quickly moves the mouse over the element.
-->
<svelte:options
  customElement={{
    tag: "oc-tooltip-v1",
    /*                                            */
    extend: window.__components.extend(),
    props: {
      position: { type: "String" },
    },
  }}
/>

<script lang="ts">
  /*                                            */
  import { on } from "svelte/events";
  /*                                            */
  import { MediaQuery } from "svelte/reactivity";

  import { onMount } from "svelte";
  import { longPressEvent } from "../../../common/actions/longPressEvent";
  import type { Props } from "./TooltipV1.types";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import Popover from "../../../common/components/Popover/Popover.svelte";
  import { PopoverPosition } from "../../../common/components/Popover/Popover.types";
  import Bubble from "../../../common/components/Popover/Bubble.svelte";

  /*        */
  /*                                                                */
  /*                                                                                     */
  /*                                */
  const isHoverable = new MediaQuery("(hover: hover)");

  let { position = "bottom", disabled = false }: Props = $props();

  let root = $state<HTMLElement>(); /*                 */
  let contextElement = $state<HTMLElement>();
  let tooltipOpen = $state(false);

  let showDelayTimeout: ReturnType<typeof setTimeout> | null = null;

  function open(): void {
    if (disabled) return;
    if (!showDelayTimeout) {
      showDelayTimeout = setTimeout(() => {
        tooltipOpen = true;
        showDelayTimeout = null;
      }, 500); /*                                                                                          */
    }
  }

  const handleOpen = (): unknown => {
    return isHoverable.current && open();
  };

  const close = (): void => {
    if (showDelayTimeout) {
      clearTimeout(showDelayTimeout);
      showDelayTimeout = null;
    }
    tooltipOpen = false;
  };

  onMount(() => {
    /*                                                     */
    const unsub = on(
      document,
      "pointerdown",
      (e) => {
        if (!tooltipOpen) return;
        const target = e.target as Node | null;

        /*                                                                           */
        if (root && target && root.contains(target)) return;
        close();
      },
      { capture: true, passive: true },
    );

    return () => {
      unsub();
    };
  });
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- eslint-disable-next-line svelte/valid-compile -->
<!-- svelte-ignore a11y_mouse_events_have_key_events -->
<!-- eslint-disable svelte/no-at-html-tags -->
<div
  class="tooltip-box"
  onmouseover={handleOpen}
  onmouseleave={close}
  onfocusin={handleOpen}
  onfocusout={close}
  use:longPressEvent={500}
  onlongpress={open}
  onkeydown={({ code }) => {
    if (tooltipOpen && code === "Escape") {
      close();
    }
  }}
  bind:this={contextElement}
>
  <slot />
</div>
<Popover
  show={tooltipOpen}
  preferredPosition={position as PopoverPosition}
  {contextElement}
  bubble={Bubble}
>
  <div>
    <slot name="tooltip-content" />
  </div>
</Popover>

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  :global {
    :host {
      display: block;
      /*                                                                                                                                 */
      width: fit-content;

      /*                                                             */
      --popover-frame-spacing: #{tokens.$oc-component-tooltip-frame-spacing};
      --popover-max-width: #{tokens.$oc-component-tooltip-max-width};
      --popover-transition-show: opacity
        #{tokens.$oc-component-tooltip-show-hide-transition-duration}
        #{tokens.$oc-component-tooltip-show-hide-transition-easing} 0.05s; /*                                                                                      */
      --popover-transition-hide: opacity
        #{tokens.$oc-component-tooltip-show-hide-transition-duration}
        #{tokens.$oc-component-tooltip-show-hide-transition-easing}
        #{tokens.$oc-component-tooltip-hide-delay};

      /*                                                                       */
      /*                 */

      /*                                      */
      --bubble-background-color: #{tokens.$oc-component-tooltip-background-color};
      --bubble-copy-color: #{tokens.$oc-component-tooltip-copy-color};
      --bubble-border-radius: #{tokens.$oc-component-tooltip-border-radius};
      --bubble-padding: #{tokens.$oc-component-tooltip-spacing-y}
        #{tokens.$oc-component-tooltip-spacing-x};
      --bubble-copy-font: #{tokens.$oc-component-tooltip-copy-font};
      --bubble-arrow-width: #{tokens.$oc-component-tooltip-arrow-width};
      --bubble-arrow-height: #{tokens.$oc-component-tooltip-arrow-height};
      --bubble-context-spacing: #{tokens.$oc-component-tooltip-context-spacing};
    }
  }
</style>
